import React, { useEffect, useState } from 'react';
import './AdminIntercomPage.scss';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import SimpleBar from 'simplebar-react';
import { format } from 'date-fns';
import type { IntercomContact } from '@brand24-original/llm-types';
import { SearchFilter } from '../../../shared/components/search-filter/SearchFilter';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import Loader from '../../../shared/components/common/loader/Loader';
import { useGetIntercomContacts } from '../../../shared/api/hooks/admin/useGetIntercomContacts';
import { useGetIntercomContact } from '../../../shared/api/hooks/admin/useGetIntercomContact';
import { useGetIntercomTags } from '../../../shared/api/hooks/admin/useGetIntercomTags';

/** Tyle tagów widać w komórce; reszta siedzi w dymku pod „+N". */
const VISIBLE_TAGS = 2;

const formatDate = (value: string | null): string =>
  value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '-';

const initialsOf = (contact: IntercomContact): string => {
  const source = contact.name || contact.email || contact.id;
  const words = source.trim().split(/\s+/);
  const letters = words.length > 1 ? `${words[0][0]}${words[1][0]}` : source.slice(0, 2);
  return letters.toUpperCase();
};

const AdminIntercomPage: React.FC = () => {
  const [filter, setFilter] = useState('');
  const [query, setQuery] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<IntercomContact | null>(null);

  const { contacts, isAvailable, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetIntercomContacts({ query, tagIds });
  const availableTags = useGetIntercomTags();
  const { contact: detail, isLoading: isDetailLoading } = useGetIntercomContact(selected?.id ?? null);

  // Wpisywanie nie odpytuje serwera przy każdej literze — filtr jedzie do zapytania po chwili
  // ciszy. Bez tego „ala" to cztery żądania do Intercoma, a jego limit jest minutowy.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = filter.trim();
      setQuery((current) => {
        if (current === next) {
          return current;
        }
        // Zmiana filtra zaczyna listę od zera — dociągnięte strony dotyczą poprzedniego zapytania.
        // Robi to za nas klucz zapytania; tu zostaje tylko wyczyszczenie zaznaczenia.
        setSelected(null);
        return next;
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [filter]);

  // Filtr tagów działa od razu; lista startuje od zera, więc zaznaczenie traci sens.
  useEffect(() => {
    setSelected(null);
  }, [tagIds]);

  /**
   * Zrzut WSZYSTKICH dociągniętych kontaktów do JSONL — ten sam format co skrypt czyszczący, więc
   * pliki z obu źródeł da się porównać albo skleić. Bierze to, co już jest w pamięci: zero
   * dodatkowych żądań, więc doładuj tyle stron, ile chcesz mieć w pliku.
   */
  const downloadBackup = (): void => {
    const url = URL.createObjectURL(
      new Blob([contacts.map((row) => JSON.stringify(row)).join('\n')], {
        type: 'application/x-ndjson',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `intercom-contacts-${new Date().toISOString().slice(0, 10)}.jsonl`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Dwa tagi w komórce, reszta pod „+N" w dymku. Intercom oddaje na liście najwyżej dziesięć tagów,
   * a `tagsTotal` mówi, ile ich jest naprawdę — więc „+N" liczymy z sumy, a w dymku pokazujemy te
   * nazwy, które mamy; brakujące ponad dziesiątkę zaznaczamy liczbą.
   */
  const tagsTemplate = (row: IntercomContact) => {
    if (row.tagsTotal === 0) {
      return '-';
    }
    const visible = row.tags.slice(0, VISIBLE_TAGS);
    const rest = row.tags.slice(VISIBLE_TAGS);
    const unnamed = row.tagsTotal - row.tags.length;
    const hidden = row.tagsTotal - visible.length;
    const tooltip = [...rest, unnamed > 0 ? `+${unnamed} more` : ''].filter(Boolean).join(', ');
    return (
      <div className="admin-intercom__pills admin-intercom__pills--cell">
        {visible.map((tag) => (
          <span key={tag} className="admin-intercom__pill">
            {tag}
          </span>
        ))}
        {hidden > 0 && (
          <span
            className="admin-intercom__pill admin-intercom__pill--muted admin-intercom__tags-more"
            data-pr-tooltip={tooltip}
          >
            +{hidden}
          </span>
        )}
      </div>
    );
  };

  // Bez dostępu do Intercoma wyszukiwarka jest atrapą — cokolwiek by wpisać, odpowiedź będzie ta
  // sama. Zamiast dawać pole, które nic nie robi, mówimy wprost, co się stało.
  if (!isLoading && !isAvailable) {
    return (
      <div className="admin-intercom">
        <EmptyPanel
          size="l"
          textKey="No connection to Intercom"
          descriptionKey="Check <code>INTERCOM_API_ACCESS_TOKEN</code> on this environment. Error details are in the backend logs."
        />
      </div>
    );
  }

  const attributes = Object.entries(detail?.customAttributes ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const tags = detail?.tags ?? selected?.tags ?? [];

  return (
    <div className="admin-intercom">
      <div className="admin-intercom__card-head">
        <div className="admin-intercom__card-title">
          Intercom contacts
          <span className="admin-intercom__card-count">
            {contacts.length}
            {hasNextPage ? '+' : ''}
          </span>
        </div>
        <div className="admin-intercom__actions">
          <SearchFilter value={filter} onChange={setFilter} placeholder="Name or e-mail…" />
          <MultiSelect
            value={tagIds}
            onChange={(e) => setTagIds((e.value as string[]) ?? [])}
            options={availableTags}
            optionLabel="name"
            optionValue="id"
            placeholder="Tags"
            filter
            showSelectAll={false}
            maxSelectedLabels={1}
            selectedItemsLabel="{0} tags"
            className="dropdown dropdown--multi-select admin-intercom__tags-filter"
          />
          <Button
            label={`Export JSONL (${contacts.length})`}
            onClick={downloadBackup}
            disabled={contacts.length === 0}
            className="btn btn--sm"
          />
        </div>
      </div>

      <div className="admin-intercom__table table-content--simplebar">
        <Tooltip target=".admin-intercom__tags-more" position="top" />
        <SimpleBar autoHide={false}>
          <DataTable
          value={contacts}
          selectionMode="single"
          selection={selected}
          onSelectionChange={(e) => setSelected((e.value as IntercomContact | null) ?? null)}
          dataKey="id"
          // Kontakt bez odpowiednika u nas (mail ani konto z external_id) — żółte tło wiersza.
          rowClassName={(row: IntercomContact) => (row.inDatabase ? '' : 'admin-intercom__row--missing')}
          scrollable
          stripedRows
          tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--stack admin-intercom__contacts"
          responsiveLayout="stack"
          loading={isLoading}
          emptyMessage={() => (
            <EmptyPanel
              size="xs"
              textKey="No contacts"
              descriptionKey="Nothing in Intercom matches this filter."
            />
          )}
        >
          <Column
            field="name"
            header="Name"
            headerClassName="table__header"
            bodyClassName="table__cell--stack-title"
            style={{ minWidth: '260px' }}
          />
          <Column
            field="email"
            header="E-mail"
            headerClassName="table__header"
            bodyClassName="table__cell--stack-wide"
            style={{ minWidth: '240px' }}
          />
          <Column
            header="Created"
            body={(row: IntercomContact) => formatDate(row.createdAt)}
            headerClassName="table__header"
            style={{ width: '160px' }}
          />
          <Column
            header="Tags"
            body={tagsTemplate}
            headerClassName="table__header"
            bodyClassName="table__cell--stack-wide"
            style={{ minWidth: '200px' }}
          />
          </DataTable>
        </SimpleBar>
      </div>

      {/*
        Doładowywanie zamiast stron: Intercom stronicuje kursorem, więc liczby stron i tak nie znamy,
        a przy dopisywaniu do listy eksport obejmuje wszystko, co użytkownik zdążył dociągnąć.
      */}
      {hasNextPage && (
        <div className="admin-intercom__more">
          <Button
            label={isFetchingNextPage ? 'Loading…' : 'Load more'}
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            className="btn btn--sm btn--secondary"
          />
        </div>
      )}

      {/* Szczegóły kontaktu w szufladzie od prawej — ta sama, co historia na stronie konta (V2). */}
      <Sidebar
        visible={selected !== null}
        onHide={() => setSelected(null)}
        position="right"
        showCloseIcon={false}
        className="admin-intercom-drawer"
        maskClassName="admin-intercom-drawer-mask"
        transitionOptions={{ classNames: 'admin-intercom-drawer-slide', timeout: 250 }}
      >
        {selected && (
          <>
            <div className="admin-intercom-drawer__head">
              <div className="admin-intercom__card-title">Contact</div>
              <button
                type="button"
                className="action-btn"
                title="Close"
                onClick={() => setSelected(null)}
              >
                <span className="ico--close ico--xs" />
              </button>
            </div>
            <div className="admin-intercom__identity">
              <div className="admin-intercom__avatar">{initialsOf(selected)}</div>
              <div className="admin-intercom__identity-text">
                <div className="admin-intercom__name">{selected.name || selected.email || selected.id}</div>
                <div className="admin-intercom__identity-meta">
                  {selected.email ?? '-'} · {selected.role ?? '-'} · id {selected.id}
                </div>
              </div>
            </div>
            <SimpleBar className="admin-intercom-drawer__scroll" autoHide={false}>
              <div className="admin-intercom-drawer__body">
                {isDetailLoading && <Loader />}
                <div className="admin-intercom__section">
                  <div className="admin-intercom__section-title">
                    Tags
                    <span className="admin-intercom__card-count">{detail?.tagsTotal ?? selected.tagsTotal}</span>
                  </div>
                  {tags.length > 0 ? (
                    <div className="admin-intercom__pills">
                      {tags.map((tag) => (
                        <span key={tag} className="admin-intercom__pill">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="admin-intercom__empty">No tags</div>
                  )}
                </div>
                <div className="admin-intercom__section">
                  <div className="admin-intercom__section-title">
                    Attributes
                    <span className="admin-intercom__card-count">{attributes.length}</span>
                  </div>
                  <div className="admin-intercom__kv">
                    <div className="admin-intercom__kv-row">
                      <span className="admin-intercom__kv-label">external_id</span>
                      <span className="admin-intercom__kv-value">{selected.externalId ?? '-'}</span>
                    </div>
                    <div className="admin-intercom__kv-row">
                      <span className="admin-intercom__kv-label">In our database</span>
                      <span className="admin-intercom__kv-value">
                        {selected.inDatabase ? (
                          'yes'
                        ) : (
                          <span className="admin-intercom__pill admin-intercom__pill--warning">not in DB</span>
                        )}
                      </span>
                    </div>
                    <div className="admin-intercom__kv-row">
                      <span className="admin-intercom__kv-label">Role</span>
                      <span className="admin-intercom__kv-value">{selected.role ?? '-'}</span>
                    </div>
                    <div className="admin-intercom__kv-row">
                      <span className="admin-intercom__kv-label">Address</span>
                      <span className="admin-intercom__kv-value">
                        {selected.isSynthetic ? (
                          <span className="admin-intercom__pill admin-intercom__pill--muted">synthetic</span>
                        ) : (
                          'real'
                        )}
                      </span>
                    </div>
                    <div className="admin-intercom__kv-row">
                      <span className="admin-intercom__kv-label">Created</span>
                      <span className="admin-intercom__kv-value">{formatDate(selected.createdAt)}</span>
                    </div>
                    <div className="admin-intercom__kv-row">
                      <span className="admin-intercom__kv-label">Last seen</span>
                      <span className="admin-intercom__kv-value">{formatDate(selected.lastSeenAt)}</span>
                    </div>
                    {attributes.map(([name, value]) => (
                      <div key={name} className="admin-intercom__kv-row">
                        <span className="admin-intercom__kv-label">{name}</span>
                        <span className="admin-intercom__kv-value">{value === null ? '-' : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SimpleBar>
          </>
        )}
      </Sidebar>
    </div>
  );
};

export default AdminIntercomPage;
